/** The sandbox state of an operator page's tenant (`GET …/sandbox/`), shared by the page's banners and
 *  its Sandbox tab (one query key, one request).
 *
 *  Asked only where there is something to show — a sandbox, or a production operator paired with one
 *  (its production-key banner) — and only for ops who may read it (`console_sandbox_view`): every
 *  other operator page makes no request. */
import { useQuery } from '@tanstack/react-query';

import { sandboxApi, sandboxKeys } from '@/api/sandbox';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { usePermissions } from '@/hooks/usePermissions';

export function isSandbox(to: Pick<TourOperatorDetail, 'environment'>): boolean {
  return to.environment === 'sandbox';
}

/** A sandbox, or a production operator with one: the pages that have sandbox state to show. */
export function hasSandboxPair(to: Pick<TourOperatorDetail, 'environment' | 'sandbox'>): boolean {
  return isSandbox(to) || to.sandbox != null;
}

export function useSandboxState(to: Pick<TourOperatorDetail, 'id' | 'environment' | 'sandbox'>) {
  const { hasPerm } = usePermissions();
  return useQuery({
    queryKey: sandboxKeys.state(to.id),
    queryFn: () => sandboxApi.state(to.id),
    enabled: hasPerm('console_sandbox_view') && hasSandboxPair(to),
  });
}
